"use client";

import { useTranslations } from "next-intl";
import { useTransition } from "react";

import { FlyInChip } from "@/components/ui/fly-in-chip";
import { usePathname, useRouter } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

interface TagFilterChipsProps {
  tags: string[];
  selected: string[];
  query?: string;
  className?: string;
  logSearches?: boolean;
  trailingContent?: React.ReactNode;
}

export function TagFilterChips({
  tags,
  selected,
  query,
  className,
  logSearches,
  trailingContent,
}: TagFilterChipsProps) {
  const t = useTranslations("discover");
  const router = useRouter();
  const pathname = usePathname();
  const [, startTransition] = useTransition();

  if (tags.length === 0 && !trailingContent) return null;

  const selectedSet = new Set(selected);
  const allActive = selectedSet.size === 0;

  const navigate = (next: string[]) => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (next.length > 0) params.set("tags", next.join(","));
    const target = params.toString() ? `${pathname}?${params}` : pathname;
    startTransition(() => {
      router.replace(target, { scroll: false });
    });
  };

  const handleChipTap = (tag: string) => {
    if (logSearches) {
      fetch("/api/tags/log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tagName: tag, eventType: "chip_tap" }),
      }).catch(() => {});
    }
  };

  return (
    <div
      role="toolbar"
      aria-label={t("tagFilterLabel")}
      className={cn(
        "-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-none md:mx-0 md:flex-wrap md:overflow-visible md:px-0",
        className
      )}
    >
      <FlyInChip index={0}>
        <Chip
          active={allActive}
          onClick={() => {
            if (!allActive) navigate([]);
          }}
          label={t("filterAll")}
        />
      </FlyInChip>
      {tags.map((tag, i) => {
        const active = selectedSet.has(tag);
        return (
          <FlyInChip key={tag} index={i + 1}>
            <Chip
              active={active}
              label={tag}
              onClick={() => {
                handleChipTap(tag);
                const next = active ? selected.filter((s) => s !== tag) : [...selected, tag];
                navigate(next);
              }}
            />
          </FlyInChip>
        );
      })}
      {trailingContent}
    </div>
  );
}

function Chip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "shrink-0 cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
        active
          ? "border-foreground bg-foreground text-background"
          : "border-input-border text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      {label}
    </button>
  );
}
